import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Gap, GapHead } from '../../ui/Gaps'
import {
  formatAmount,
  formatDate,
  formatDayTime,
  formatPhone,
  isoDay,
  nowMs,
} from '@dorixona/utils'
import { DetailHead } from '../../ui/DetailHead'
import { Icon } from '../../ui/Icon'
import { Failed, Loading, failureText } from '../../ui/Load'
import { Modal } from '../../ui/Modal'
import { Pagination } from '../../ui/Pagination'
import { TableSkeleton } from '../../ui/Skeleton'
import { initials } from '../auth/model'
import { useSession } from '../auth/session'
import { useOrders, type Order } from '../orders/api'
import { STATUS_TONE, shipText, statusText } from '../orders/model'
import {
  useBlockCustomer,
  useCustomer,
  useCustomerAddresses,
  useCustomerOrderCounts,
  useEraseCustomer,
  type Customer,
  type LedgerEntry,
} from './api'
import {
  LEDGER_LIMIT,
  birthText,
  deletionText,
  displayName,
  genderText,
  isDeleted,
  langText,
  ledgerCount,
  ledgerNote,
  ledgerText,
  ledgerTotals,
  notifyText,
  sourceText,
} from './model'
import { listAddress } from '../../app/listMemory'

export interface CustomerScreenProps {
  onToast: (message: string) => void
}

type Tab = 'over' | 'orders' | 'tx' | 'addr'

/** Сколько строк показывают превью «Обзора»; остальное — во вкладках. */
const RECENT = 5
const PAGE_SIZES = [10, 25, 50] as const

/** Где взять недостающие числа клиента: в карточке `GET /admin/customers/{id}`. */
const CARD_API = 'GET /admin/customers/{id}'

type OrdersQuery = ReturnType<typeof useOrders>
type CountsQuery = ReturnType<typeof useCustomerOrderCounts>

/**
 * Карточка клиента: деньги, баллы, заказы и адреса. Профиль ведёт сам клиент
 * в приложении, админка его читает и правит только по обращению (В-39).
 *
 * Числа — только серверные: баланс из профиля, счётчики — `total` списка
 * заказов с фильтром. Сумм по заказам сервер не отдаёт, а считать их по
 * одной странице значит показать неправду — такие плитки помечены «нет в API».
 */
export function CustomerScreen({ onToast }: CustomerScreenProps) {
  const { can } = useSession()
  const navigate = useNavigate()
  const params = useParams()
  const [tab, setTab] = useState<Tab>('over')
  const [confirmBlock, setConfirmBlock] = useState(false)
  const [confirmErase, setConfirmErase] = useState(false)
  const id = Number(params.id)
  // Без права на заказы нет ни вкладки, ни сумм покупок: запрос не уходит,
  // и «загружаем» висело бы вечно.
  const withOrders = can('orders')

  const card = useCustomer(id)
  // Пять последних заказов для «Обзора»; `total` того же ответа — число у вкладки.
  const orders = useOrders({ customerId: id, pageSize: RECENT }, withOrders && id > 0)
  const counts = useCustomerOrderCounts(id, withOrders && id > 0)
  const addresses = useCustomerAddresses(id, tab === 'addr')
  const block = useBlockCustomer()
  const erase = useEraseCustomer()

  const toList = () => {
    void navigate(listAddress('/customers'))
  }

  if (card.isPending) return <Loading what="клиента" />
  if (card.isError) {
    return (
      <div className="ui-card">
        <div className="ui-empty">
          <Icon name="i-users" />
          <b>Клиент не найден</b>
          <p>{failureText(card.error)}</p>
          <button type="button" className="ui-btn" data-variant="secondary" onClick={toList}>
            К списку клиентов
          </button>
        </div>
      </div>
    )
  }

  const { customer, balance, ledger } = card.data
  const name = displayName(customer)
  // Удалённый — только для просмотра: правку и блокировку сервер отклонит
  // (404), поэтому кнопок нет заранее. Стереть данные можно, пока не стёрты.
  const deleted = isDeleted(customer)
  const canEdit = can('customers', 'u') && !deleted
  const canErase = can('customers', 'd') && !customer.anonymizedAt
  // У адресов итога без запроса нет — число не показываем, а не выдумываем.
  const tabs: [Tab, string, number | string | null][] = [
    ['over', 'Обзор', null],
    ['orders', 'Заказы', orders.data?.total ?? null],
    ['tx', 'Транзакции', ledgerCount(ledger.length)],
    ['addr', 'Адреса', null],
  ]
  const shownTabs = tabs.filter(([key]) => withOrders || key !== 'orders')

  function setBlocked(blocked: boolean) {
    block.mutate(
      { id, blocked },
      {
        onSuccess: () => {
          onToast(
            blocked
              ? `${name}: доступ в приложение закрыт, сеансы завершены`
              : `${name}: доступ в приложение открыт`,
          )
        },
        onError: (error) => {
          onToast(failureText(error))
        },
        // Окно закрываем, только когда сервер ответил: пока идёт запрос,
        // кнопка занята и повторно не нажимается.
        onSettled: () => {
          setConfirmBlock(false)
        },
      },
    )
  }

  function eraseData() {
    erase.mutate(id, {
      onSuccess: () => {
        onToast(`${name}: персональные данные стёрты`)
      },
      onError: (error) => {
        onToast(failureText(error))
      },
      onSettled: () => {
        setConfirmErase(false)
      },
    })
  }

  return (
    <>
      <DetailHead
        backLabel="Клиенты"
        onBack={toList}
        title={name}
        avatar={
          <span className="ui-avatar" data-size="lg" aria-hidden="true">
            {initials(name)}
          </span>
        }
        sub={
          <>
            <span className="num">{formatPhone(customer.phone)}</span>
            <span className="dot" aria-hidden="true" />
            <span className="num">
              в программе с {customer.registered ? formatDate(customer.registered) : '—'}
            </span>
            <span className="ui-tag">{sourceText(customer.telegram)}</span>
            {/* Заблокированный не войдёт в приложение (BR-06) — это состояние,
                поэтому статус, как в реестре, а не метка. Удаление важнее
                блокировки: войти уже некому. */}
            {deleted ? (
              <span className="ui-status" data-tone="neutral">
                {deletionText(customer)}
              </span>
            ) : customer.blocked ? (
              <span className="ui-status" data-tone="danger">
                заблокирован
              </span>
            ) : null}
          </>
        }
        actions={
          canEdit || canErase ? (
            <>
              {canEdit ? (
                <>
                  <button
                    type="button"
                    className="ui-btn"
                    data-variant="secondary"
                    onClick={() => {
                      void navigate(`/customers/${customer.id}/edit`)
                    }}
                  >
                    <Icon name="i-edit" />
                    Изменить данные
                  </button>
                  {/* Кнопка блокировки одна и зависит от того, что сейчас. Блокировка
                  завершает сеансы клиента — её переспрашиваем; разблокировка
                  ничего не отнимает и срабатывает сразу. */}
                  <button
                    type="button"
                    className="ui-btn"
                    data-variant="secondary"
                    disabled={block.isPending}
                    onClick={() => {
                      if (customer.blocked) setBlocked(false)
                      else setConfirmBlock(true)
                    }}
                  >
                    {customer.blocked ? 'Разблокировать' : 'Заблокировать'}
                  </button>
                </>
              ) : null}
              {canErase ? (
                <button
                  type="button"
                  className="ui-btn"
                  data-variant="secondary"
                  onClick={() => {
                    setConfirmErase(true)
                  }}
                >
                  <Icon name="i-trash" />
                  Стереть данные
                </button>
              ) : null}
            </>
          ) : null
        }
      />

      <div className="ui-tabs ui-detail-tabs" role="tablist" aria-label="Разделы карточки">
        {shownTabs.map(([key, name, count]) => (
          <button
            key={key}
            type="button"
            role="tab"
            className="ui-tab"
            aria-selected={tab === key}
            onClick={() => {
              setTab(key)
            }}
          >
            {name}
            {count === null ? null : <span className="ui-count">{count}</span>}
          </button>
        ))}
      </div>

      {tab === 'over' ? (
        <div className="ui-stack">
          <CustomerStats
            balance={balance}
            ledger={ledger}
            orders={withOrders ? orders : null}
            counts={withOrders ? counts : null}
          />
          <div className="ui-detail-grid">
            <div className="ui-detail-col">
              {withOrders ? (
                <RecentOrders
                  orders={orders}
                  onAll={() => {
                    setTab('orders')
                  }}
                />
              ) : null}
              <RecentLedger
                ledger={ledger}
                onAll={() => {
                  setTab('tx')
                }}
              />
            </div>
            <div className="ui-detail-col">
              <CustomerFacts customer={customer} orders={withOrders ? orders : null} />
              <LoyaltyCard customer={customer} balance={balance} />
            </div>
          </div>
        </div>
      ) : null}

      {tab === 'orders' ? <CustomerOrders id={id} /> : null}
      {tab === 'tx' ? <LedgerTable ledger={ledger} /> : null}

      {tab === 'addr' ? (
        <section className="ui-table-card" aria-labelledby="cus-addr">
          <div className="ui-card-head">
            <h3 id="cus-addr">Адреса доставки</h3>
            <span className="aside">
              <Icon name="i-phone" />
              ведёт клиент в приложении
            </span>
          </div>
          {addresses.isPending ? <TableSkeleton what="адреса" rows={2} /> : null}
          {addresses.isError ? (
            <Failed
              what="адреса"
              error={addresses.error}
              onRetry={() => {
                void addresses.refetch()
              }}
            />
          ) : null}
          {addresses.data?.length === 0 ? (
            <div className="ui-empty">
              <Icon name="i-pin" />
              <b>Клиент пока не сохранял адреса</b>
            </div>
          ) : null}
          {addresses.data?.length ? (
            <div className="ui-table-wrap">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th scope="col">Адрес</th>
                    <th scope="col">Последний раз</th>
                  </tr>
                </thead>
                <tbody>
                  {addresses.data.map((address) => (
                    <tr key={address.id}>
                      <td>
                        <span className="cus-addr">
                          {address.label}
                          {address.def ? <span className="ui-tag">по умолчанию</span> : null}
                        </span>
                      </td>
                      <td className="num cus-dim">
                        {address.used ? formatDate(address.used) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}

      {confirmBlock ? (
        <Modal
          title="Заблокировать клиента?"
          onClose={() => {
            setConfirmBlock(false)
          }}
        >
          <p>
            {name} не сможет войти в приложение, открытые сеансы завершатся. Разблокировать можно в
            любой момент.
          </p>
          <div className="mfoot">
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              onClick={() => {
                setConfirmBlock(false)
              }}
            >
              Не блокировать
            </button>
            <button
              type="button"
              className="ui-btn"
              data-variant="danger"
              disabled={block.isPending}
              aria-busy={block.isPending}
              onClick={() => {
                setBlocked(true)
              }}
            >
              Заблокировать
            </button>
          </div>
        </Modal>
      ) : null}

      {confirmErase ? (
        <Modal
          title="Стереть данные клиента?"
          onClose={() => {
            setConfirmErase(false)
          }}
        >
          <p>
            Имя, телефон и адреса {name} удалятся навсегда. Заказы и баллы останутся в истории без
            имени.
          </p>
          <div className="mfoot">
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              onClick={() => {
                setConfirmErase(false)
              }}
            >
              Не стирать
            </button>
            <button
              type="button"
              className="ui-btn"
              data-variant="danger"
              disabled={erase.isPending}
              aria-busy={erase.isPending}
              onClick={eraseData}
            >
              Стереть данные
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  )
}

/** Число из запроса: пока идёт — полоска, не пришло — прочерк, а не ноль. */
function Pending({ failed }: { failed: boolean }) {
  return failed ? <>—</> : <span className="ui-skeleton cus-skel" />
}

/**
 * Показатели четырьмя плитками, как в прототипе: число крупно, под ним —
 * из чего оно сложено. Баллы — обычным цветом: жёлтый в админке только у
 * очереди. Суммы покупок сервер не считает — это одна плитка «нет в API»,
 * а не три пустых. `orders: null` — роль не видит заказы.
 */
function CustomerStats({
  balance,
  ledger,
  orders,
  counts,
}: {
  balance: number
  ledger: LedgerEntry[]
  orders: OrdersQuery | null
  counts: CountsQuery | null
}) {
  const points = ledgerTotals(ledger)
  // Лента обрезана на 50 движениях — итог не за всё время, и это видно.
  const scope = points.partial ? ' · по последним 50' : ''
  return (
    <section className="ui-tiles" aria-label="Показатели клиента">
      <div className="ui-tile">
        <span className="lbl">Баланс баллов</span>
        <span className="val">{formatAmount(balance)}</span>
        <span className="sub">
          начислено {formatAmount(points.earned)}
          {scope}
        </span>
      </div>
      {orders && counts ? (
        <div className="ui-tile">
          <span className="lbl">Заказов</span>
          <span className="val">
            {orders.data ? orders.data.total : <Pending failed={orders.isError} />}
          </span>
          <span className="sub">
            {counts.data ? (
              `завершено ${counts.data.done} · не состоялось ${counts.data.lost}`
            ) : (
              <Pending failed={counts.isError} />
            )}
          </span>
        </div>
      ) : null}
      <div className="ui-tile">
        <span className="lbl">Списано баллами</span>
        <span className="val">{formatAmount(points.spent)}</span>
        <span className="sub">
          {points.earned ? Math.round((points.spent / points.earned) * 100) : 0}% от начисленного
          {scope}
        </span>
      </div>
      <div className="ui-tile" data-gap>
        <span className="lbl">
          Покупок на сумму
          <Gap what="Сумма покупок и средний чек клиента" where={CARD_API} />
        </span>
        <span className="val">—</span>
        <span className="sub">и средний чек</span>
      </div>
    </section>
  )
}

/**
 * Карта лояльности: номер телефона — он же карта (по нему копят баллы на
 * кассе). Тёмная, как боковая панель, — это поверхность, а не кнопка.
 */
function LoyaltyCard({ customer, balance }: { customer: Customer; balance: number }) {
  return (
    <section className="ui-card" aria-labelledby="cus-card">
      <div className="ui-card-head">
        <h3 id="cus-card">Карта лояльности</h3>
      </div>
      <div className="ui-card-body">
        <div className="cus-loyalty">
          <span className="brand">03 Dorixona · кэшбэк</span>
          <b className="num">{formatPhone(customer.phone)}</b>
          <span className="foot">
            <span>{displayName(customer)}</span>
            <span className="num">{formatAmount(balance)} баллов</span>
          </span>
        </div>
      </div>
    </section>
  )
}

/** Получение заказа — квадратная метка с иконкой, как в списке заказов. */
function ShipMethod({ order }: { order: Order }) {
  return (
    <span className="ui-method" data-method={order.ship}>
      <Icon name={order.ship === 'delivery' ? 'i-truck' : 'i-pin'} size={14} />
      {shipText(order.ship)}
    </span>
  )
}

function OrderStatus({ order }: { order: Order }) {
  return (
    <span className="ui-status" data-tone={STATUS_TONE[order.status]}>
      {statusText(order.status)}
    </span>
  )
}

/** Заказов нет: одинаково на «Обзоре» и во вкладке. */
function NoOrders() {
  return (
    <div className="ui-empty">
      <Icon name="i-box" />
      <b>Заказов пока не было</b>
    </div>
  )
}

/** Последние заказы на «Обзоре»; полный список — во вкладке «Заказы». */
function RecentOrders({ orders, onAll }: { orders: OrdersQuery; onAll: () => void }) {
  const navigate = useNavigate()

  return (
    <section className="ui-table-card" aria-labelledby="cus-recent">
      <div className="ui-card-head">
        <h3 id="cus-recent">Последние заказы</h3>
        {orders.data?.items.length ? (
          <button
            type="button"
            className="ui-btn"
            data-variant="ghost"
            data-size="sm"
            onClick={onAll}
          >
            Все заказы
          </button>
        ) : null}
      </div>
      {orders.isPending ? <TableSkeleton what="заказы" rows={3} /> : null}
      {orders.isError ? (
        <Failed
          what="заказы"
          error={orders.error}
          onRetry={() => {
            void orders.refetch()
          }}
        />
      ) : null}
      {orders.data?.items.length === 0 ? <NoOrders /> : null}
      {orders.data?.items.length ? (
        <div className="ui-table-wrap">
          <table className="ui-table cus-recent">
            <thead>
              <tr>
                <th scope="col">Заказ</th>
                <th scope="col">Дата</th>
                <th scope="col">Получение</th>
                <th scope="col" data-align="end">
                  Сумма
                </th>
                <th scope="col" data-align="end">
                  Кэшбэк
                </th>
                <th scope="col">Статус</th>
              </tr>
            </thead>
            <tbody>
              {orders.data.items.map((order) => {
                const href = `/orders/${order.id}`
                return (
                  <tr
                    key={order.id}
                    data-href={href}
                    onClick={() => {
                      void navigate(href)
                    }}
                  >
                    <td>
                      <Link className="primary num" to={href}>
                        № {order.number}
                      </Link>
                    </td>
                    <td className="num cus-dim">{formatDate(order.createdAt)}</td>
                    <td>
                      <ShipMethod order={order} />
                    </td>
                    <td className="num" data-align="end">
                      {formatAmount(order.total)}
                    </td>
                    <td className="num" data-align="end">
                      +{formatAmount(order.cashback)}
                    </td>
                    <td>
                      <OrderStatus order={order} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  )
}

/** Сумма движения со знаком: плюс — пришло, минус — ушло. */
function Points({ amount }: { amount: number }) {
  return (
    <>
      {amount > 0 ? '+' : ''}
      {formatAmount(amount)}
    </>
  )
}

/**
 * Последние движения баллов на «Обзоре». Лента уже пришла с карточкой —
 * отдельного запроса нет, показываем первые пять.
 */
function RecentLedger({ ledger, onAll }: { ledger: LedgerEntry[]; onAll: () => void }) {
  const navigate = useNavigate()

  return (
    <section className="ui-table-card" aria-labelledby="cus-recent-tx">
      <div className="ui-card-head">
        <h3 id="cus-recent-tx">Последние транзакции</h3>
        {ledger.length ? (
          <button
            type="button"
            className="ui-btn"
            data-variant="ghost"
            data-size="sm"
            onClick={onAll}
          >
            Вся история
          </button>
        ) : null}
      </div>
      {ledger.length === 0 ? (
        <NoLedger />
      ) : (
        <div className="ui-table-wrap">
          <table className="ui-table">
            <thead>
              <tr>
                <th scope="col">Движение</th>
                <th scope="col" data-align="end">
                  Баллы
                </th>
              </tr>
            </thead>
            <tbody>
              {ledger.slice(0, RECENT).map((item) => {
                const href = item.orderId ? `/orders/${item.orderId}` : null
                return (
                  <tr
                    key={item.id}
                    data-href={href ?? undefined}
                    onClick={
                      href
                        ? () => {
                            void navigate(href)
                          }
                        : undefined
                    }
                  >
                    <td>
                      {href ? (
                        <Link className="primary" to={href}>
                          {ledgerText(item.type)}
                        </Link>
                      ) : (
                        <span className="primary">{ledgerText(item.type)}</span>
                      )}
                      <span className="secondary">
                        {ledgerNote(item.type)} · {formatDayTime(item.at)}
                      </span>
                    </td>
                    <td className="num cus-amt" data-align="end">
                      <Points amount={item.amount} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

/**
 * Профиль: только чтение — его меняет сам клиент в приложении. Номер
 * телефона — он же карта лояльности: по нему начисляют баллы на кассе.
 */
function CustomerFacts({ customer, orders }: { customer: Customer; orders: OrdersQuery | null }) {
  const last = orders?.data?.items[0]
  return (
    <section className="ui-card" aria-labelledby="cus-facts">
      <div className="ui-card-head">
        <h3 id="cus-facts">Профиль</h3>
      </div>
      <div className="ui-card-body cus-facts-body">
        <div className="ui-facts">
          <div>
            <span className="k">Телефон (ID)</span>
            <span className="v num">{formatPhone(customer.phone)}</span>
          </div>
          <div>
            <span className="k">Дата рождения</span>
            <span className="v num">{birthText(customer.birth, isoDay(nowMs()))}</span>
          </div>
          <div>
            <span className="k">Пол</span>
            <span className="v">{genderText(customer.gender)}</span>
          </div>
          <div>
            <span className="k">Язык приложения</span>
            <span className="v">{langText(customer.lang)}</span>
          </div>
          <div>
            <span className="k">В программе с</span>
            <span className="v num">
              {customer.registered ? formatDate(customer.registered) : '—'}
            </span>
          </div>
          <div>
            <span className="k">Источник</span>
            <span className="v">{sourceText(customer.telegram)}</span>
          </div>
          <div>
            <span className="k">Уведомления</span>
            <span className="v">{notifyText(customer.notifyPush)}</span>
          </div>
          {/* Последняя активность — последний заказ: входов в приложение API не
              отдаёт, а заказ и есть то, что сотруднику важно. */}
          {last ? (
            <div>
              <span className="k">Последний заказ</span>
              <span className="v num">{formatDayTime(last.createdAt)}</span>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}

/** Все заказы клиента: листает сервер. */
function CustomerOrders({ id }: { id: number }) {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [size, setSize] = useState<number>(PAGE_SIZES[0])
  const orders = useOrders({ customerId: id, page, pageSize: size }, true, true)

  return (
    <section className="ui-table-card" aria-labelledby="cus-orders">
      <div className="ui-card-head">
        <h3 id="cus-orders">
          Заказы клиента
          {orders.data?.total ? <span className="ui-count">{orders.data.total}</span> : null}
        </h3>
      </div>
      {orders.isPending ? <TableSkeleton what="заказы" /> : null}
      {orders.isError ? (
        <Failed
          what="заказы"
          error={orders.error}
          onRetry={() => {
            void orders.refetch()
          }}
        />
      ) : null}
      {orders.data?.items.length === 0 ? <NoOrders /> : null}
      {orders.data?.items.length ? (
        <>
          <div className="ui-table-wrap" aria-busy={orders.isPlaceholderData}>
            <table className="ui-table">
              <thead>
                <tr>
                  <th scope="col">Заказ</th>
                  <th scope="col">Дата</th>
                  <th scope="col">Получение</th>
                  <th scope="col" data-align="end">
                    Сумма
                  </th>
                  <th scope="col" data-align="end">
                    Кэшбэк
                  </th>
                  <th scope="col">Статус</th>
                </tr>
              </thead>
              <tbody>
                {orders.data.items.map((order) => {
                  const href = `/orders/${order.id}`
                  return (
                    <tr
                      key={order.id}
                      data-href={href}
                      onClick={() => {
                        void navigate(href)
                      }}
                    >
                      <td>
                        <Link className="primary num" to={href}>
                          № {order.number}
                        </Link>
                      </td>
                      <td className="num cus-dim">{formatDayTime(order.createdAt)}</td>
                      <td>
                        <ShipMethod order={order} />
                      </td>
                      <td className="num" data-align="end">
                        {formatAmount(order.total)}
                      </td>
                      <td className="num" data-align="end">
                        +{formatAmount(order.cashback)}
                      </td>
                      <td>
                        <OrderStatus order={order} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            label="Страницы заказов"
            page={page}
            pageSize={size}
            total={orders.data.total}
            sizes={PAGE_SIZES}
            busy={orders.isPlaceholderData}
            onPage={setPage}
            onPageSize={(next) => {
              setSize(next)
              setPage(1)
            }}
          />
        </>
      ) : null}
    </section>
  )
}

/** Движений не было: одинаково на «Обзоре» и во вкладке. */
function NoLedger() {
  return (
    <div className="ui-empty">
      <Icon name="i-coins" />
      <b>Движений баллов пока не было</b>
    </div>
  )
}

/**
 * Лента баллов приходит вместе с карточкой — последние 50 движений, без
 * страниц на сервере. Листаем уже пришедшее по 10, как любой список;
 * всю историю сервер админке не отдаёт — это помечено. Движение по заказу
 * открывает этот заказ.
 */
function LedgerTable({ ledger }: { ledger: LedgerEntry[] }) {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [size, setSize] = useState<number>(PAGE_SIZES[0])
  const rows = ledger.slice((page - 1) * size, page * size)

  return (
    <section className="ui-table-card" aria-labelledby="cus-ledger">
      <div className="ui-card-head">
        <h3 id="cus-ledger">
          История баллов
          {ledger.length ? <span className="ui-count">{ledgerCount(ledger.length)}</span> : null}
        </h3>
        {ledger.length >= LEDGER_LIMIT ? (
          <span className="aside">
            последние {LEDGER_LIMIT}
            <Gap what="Вся история баллов по страницам" where={CARD_API} />
          </span>
        ) : null}
      </div>
      {ledger.length === 0 ? (
        <NoLedger />
      ) : (
        <>
          <div className="ui-table-wrap">
            <table className="ui-table">
              <thead>
                <tr>
                  <th scope="col">Дата</th>
                  <th scope="col">Операция</th>
                  <th scope="col">Заказ</th>
                  <th scope="col" data-gap>
                    <GapHead what="Кто провёл операцию" where={CARD_API}>
                      Кто
                    </GapHead>
                  </th>
                  <th scope="col" data-align="end">
                    Баллы
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => {
                  const href = item.orderId ? `/orders/${item.orderId}` : null
                  return (
                    <tr
                      key={item.id}
                      data-href={href ?? undefined}
                      onClick={
                        href
                          ? () => {
                              void navigate(href)
                            }
                          : undefined
                      }
                    >
                      <td className="num cus-dim">{formatDayTime(item.at)}</td>
                      <td>
                        <span className="primary">{ledgerText(item.type)}</span>
                        <span className="secondary">{ledgerNote(item.type)}</span>
                      </td>
                      <td>
                        {href ? (
                          <Link className="primary" to={href}>
                            открыть
                          </Link>
                        ) : (
                          <span className="cus-dim">—</span>
                        )}
                      </td>
                      <td className="cus-dim" data-gap>
                        —
                      </td>
                      <td className="num cus-amt" data-align="end">
                        <Points amount={item.amount} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            label="Страницы истории баллов"
            page={page}
            pageSize={size}
            total={ledger.length}
            sizes={PAGE_SIZES}
            onPage={setPage}
            onPageSize={(next) => {
              setSize(next)
              setPage(1)
            }}
          />
        </>
      )}
    </section>
  )
}
